// D45 — Page /maintenance : liste interventions + KPI + filtres + tableau
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { interventionsApi, exportsApi } from "@/lib/api";
import type { TypeMaintenance, StatutIntervention } from "@/types/intervention";
import { Button } from "@/components/ui/button";
import { ClipboardList, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/lib/i18n";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";

// P25-i18n : utilise t() via closure (les labels sont rendus dans le composant)
function statutLabel(s: StatutIntervention, t: (k: string) => string): string {
  return t(`statut.${s}`);
}
function typeLabel(tp: TypeMaintenance, t: (k: string) => string): string {
  return t(`type.${tp}`);
}

export default function ListeInterventionsPage() {
  const t = useT();
  const [filterType, setFilterType] = useState<TypeMaintenance | "">("");
  const [filterStatut, setFilterStatut] = useState<StatutIntervention | "">("");
  const [filterEnginId, setFilterEnginId] = useState<number | "">("");
  const [filterSiteId, setFilterSiteId] = useState<number | "">("");
  const [filterContratId, setFilterContratId] = useState<number | "">("");
  const [filterDu, setFilterDu] = useState("");
  const [filterAu, setFilterAu] = useState("");
  const [tri, setTri] = useState("date_debut");
  const [ordre, setOrdre] = useState("desc");

  // Listes pour selects (P23 — données pour filtres)
  const { data: engins } = useQuery({
    queryKey: ["engins"],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/v1/engins`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
  });
  const { data: sites } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/v1/sites`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
  });
  const { data: contrats } = useQuery({
    queryKey: ["contrats"],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/v1/contrats`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "interventions",
      {
        filterType,
        filterStatut,
        filterEnginId,
        filterSiteId,
        filterContratId,
        filterDu,
        filterAu,
        tri,
        ordre,
      },
    ],
    queryFn: () =>
      interventionsApi.list({
        type: filterType || undefined,
        statut: filterStatut || undefined,
        engin_id: filterEnginId || undefined,
        site_id: filterSiteId || undefined,
        contrat_id: filterContratId || undefined,
        du: filterDu || undefined,
        au: filterAu || undefined,
        tri,
        ordre,
      }),
  });

  if (error) {
    return (
      <div className="p-8 text-red-600">
        Erreur : {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("maintenance.title")}</h1>
          <p className="text-muted-foreground">{t("maintenance.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() =>
              window.open(
                exportsApi.interventionsPdfUrl({
                  type: filterType || undefined,
                  statut: filterStatut || undefined,
                }),
                "_blank",
              )
            }
          >
            {t("maintenance.export_pdf")}
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              window.open(exportsApi.ficheViergeUrl(), "_blank")
            }
          >
            {t("maintenance.fiche_vierge")}
          </Button>
          <Link to="/maintenance/nouveau">
            <Button>+ {t("maintenance.new")}</Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      {data?.kpi && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <KpiCard label={t("maintenance.kpi.total")} value={data.kpi.total} />
          <KpiCard label={t("maintenance.kpi.preventives")} value={data.kpi.preventives} variant="success" />
          <KpiCard label={t("maintenance.kpi.curatives")} value={data.kpi.curatives} variant="warning" />
          <KpiCard label={t("maintenance.kpi.terminees")} value={data.kpi.terminees} variant="info" />
          <KpiCard label={t("maintenance.kpi.avec_da")} value={data.kpi.avec_da} />
        </div>
      )}

      {/* Filtres */}
      <Card>
        <CardHeader>
          <CardTitle>{t("maintenance.filters")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.type")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as TypeMaintenance | "")}
              >
                <option value="">{t("maintenance.filter.all")}</option>
                <option value="preventive">{t("type.preventive")}</option>
                <option value="curative">{t("type.curative")}</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.statut")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterStatut}
                onChange={(e) =>
                  setFilterStatut(e.target.value as StatutIntervention | "")
                }
              >
                <option value="">{t("maintenance.filter.all")}</option>
                <option value="planned">{t("statut.planned")}</option>
                <option value="in_progress">{t("statut.in_progress")}</option>
                <option value="completed">{t("statut.completed")}</option>
                <option value="cancelled">{t("statut.cancelled")}</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.engin")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterEnginId}
                onChange={(e) => setFilterEnginId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">{t("maintenance.filter.all")}</option>
                {(engins as Array<{ id: number; code: string }> | undefined)?.map((e) => (
                  <option key={e.id} value={e.id}>{e.code}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.site")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterSiteId}
                onChange={(e) => setFilterSiteId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">{t("maintenance.filter.all")}</option>
                {(sites as Array<{ id: number; name: string }> | undefined)?.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.contrat")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterContratId}
                onChange={(e) => setFilterContratId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">{t("maintenance.filter.all")}</option>
                {(contrats as Array<{ id: number; label: string }> | undefined)?.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.du")}</label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterDu}
                onChange={(e) => setFilterDu(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.au")}</label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterAu}
                onChange={(e) => setFilterAu(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.tri")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={tri}
                onChange={(e) => setTri(e.target.value)}
              >
                <option value="date_debut">{t("maintenance.filter.tri_date_debut")}</option>
                <option value="date_fin">{t("maintenance.filter.tri_date_fin")}</option>
                <option value="created_at">{t("maintenance.filter.tri_created_at")}</option>
                <option value="statut">{t("maintenance.filter.tri_statut")}</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t("maintenance.filter.ordre")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={ordre}
                onChange={(e) => setOrdre(e.target.value)}
              >
                <option value="asc">{t("maintenance.filter.asc")}</option>
                <option value="desc">{t("maintenance.filter.desc")}</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setFilterType("");
                  setFilterStatut("");
                  setFilterEnginId("");
                  setFilterSiteId("");
                  setFilterContratId("");
                  setFilterDu("");
                  setFilterAu("");
                  setTri("date_debut");
                  setOrdre("desc");
                }}
              >
                {t("common.reset")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card>
        <CardHeader>
          <CardTitle>{t("maintenance.list.title")} ({data?.total ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">{ht("Chargement...")}
            </div>
          ) : data && data.items.length === 0 ? (
            /* P58-C : était écrit comme texte JSX NU → l'utilisateur voyait
               littéralement t("maintenance.list.empty"). On en profite pour
               proposer une action : un état vide sans sortie n'aide pas. */
            <div
              data-testid="interventions-empty"
              className="flex flex-col items-center justify-center gap-3 py-12 text-center"
            >
              <ClipboardList className="h-10 w-10 text-muted-foreground/50" />
              <div className="space-y-1">
                <p className="text-sm font-medium">{t("maintenance.list.empty")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("maintenance.list.empty_hint")}
                </p>
              </div>
              <Link to="/maintenance/nouveau">
                <Button size="sm">
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {t("maintenance.new")}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="px-3 py-2">{t("maintenance.col.code")}</th>
                    <th className="px-3 py-2">{t("maintenance.col.engin")}</th>
                    <th className="px-3 py-2">{t("maintenance.col.type")}</th>
                    <th className="px-3 py-2">{t("maintenance.col.statut")}</th>
                    <th className="px-3 py-2">{t("maintenance.col.date_debut")}</th>
                    <th className="px-3 py-2">{t("maintenance.col.date_fin")}</th>
                    <th className="px-3 py-2">{t("maintenance.col.resume")}</th>
                    <th className="px-3 py-2">{t("maintenance.col.da")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((i) => (
                    <tr key={i.id} className="border-b hover:bg-muted/30">
                      <td className="px-3 py-2 font-mono font-semibold">
                        <Link
                          to={`/maintenance/${i.id}`}
                          className="hover:underline"
                        >
                          {i.intervention_code}
                        </Link>
                      </td>
                      <td className="px-3 py-2">#{i.engin_id}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            i.type === "preventive"
                              ? "text-green-700"
                              : "text-orange-700"
                          }
                        >
                          {typeLabel(i.type as TypeMaintenance, t) ?? i.type}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <StatutBadge statut={i.statut as StatutIntervention} t={t} />
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(i.date_debut).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(i.date_fin).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-3 py-2 max-w-xs truncate">
                        {i.resume ?? "-"}
                      </td>
                      <td className="px-3 py-2">
                        {i.demande_creaset ? (
                          <span className="rounded bg-amber-500/15 border border-amber-500/40 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300">
                            DA
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: number;
  variant?: "default" | "success" | "warning" | "info";
}) {
  const colors = {
    default: "bg-gray-50 text-gray-900",
    success: "bg-green-50 text-green-900",
    warning: "bg-orange-50 text-orange-900",
    info: "bg-blue-50 text-blue-900",
  };
  return (
    <Card>
      <CardContent className={`p-4 ${colors[variant]}`}>
        <div className="text-xs uppercase opacity-75">{label}</div>
        <div className="text-3xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function StatutBadge({ statut, t }: { statut: StatutIntervention; t: (k: string) => string }) {
  const styles: Record<StatutIntervention, string> = {
    planned: "bg-gray-100 text-gray-800",
    in_progress: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/40",
    completed: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40",
    cancelled: "bg-destructive/15 text-destructive dark:text-red-300 border border-destructive/40 line-through",
  };
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs ${styles[statut]}`}
    >
      {statutLabel(statut, t)}
    </span>
  );
}